import { useMemo, useState, useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDealContext } from "../hooks/useDealContext";
import { updateTenant, fetchDeal } from "../api/client";
import { useToastContext } from "../contexts/ToastContext";
import { friendlyError } from "../utils/errors";
import {
  DetailViewPanel,
  TimeSeriesInputTable,
  ComputedScheduleTable,
  CpiTab,
  FreeRentTab,
  PercentageRentTab,
  RolloverTab,
} from "../components/detail-views";
import type {
  TimeSeriesEntry,
  ColumnDef,
  ComputedScheduleRow,
} from "../components/detail-views/types";
import type { FreeRentPeriod } from "../components/detail-views/FreeRentTab";
import type {
  PercentageRentType,
  PercentageRentEntry,
} from "../components/detail-views/PercentageRentTab";
import type { RolloverOverrides } from "../components/detail-views/RolloverTab";

// ---------- types ----------

interface ParsedTenantRef {
  tenant_id: string;
  tenant_name: string;
  suite_id: string | null;
  /** Percentage rent fields (engine Tenant model) — present when the rent
   *  roll parser or a prior edit populated them. Rate is a decimal
   *  (0.06 = 6%); breakpoint null with a rate set = natural breakpoint. */
  percentage_rent_pct?: number | null;
  percentage_rent_breakpoint?: number | null;
  annual_sales?: number | null;
  annual_sales_growth_pct?: number | null;
}

interface RentRollDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  sorted: ParsedTenantRef[];
  detailTenantIdx: number;
  detailTab: number;
  onTabChange: (idx: number) => void;
  detailData: Record<string, unknown> | null;
  detailEntries: TimeSeriesEntry[];
  detailDirty: boolean;
  detailSaving: boolean;
  detailLoading: boolean;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
  onEntryChange: (entryId: string, key: string, value: unknown) => void;
  onEntryAdd: (template: Record<string, unknown>) => void;
  onEntryDelete: (entryId: string) => void;
  onSave: () => void;
  onReset: () => void;
}

// ---------- column definitions ----------

/** Base Rent tab columns: base rent entries and rent steps only (no free rent). */
const baseRentColumns: ColumnDef[] = [
  { key: "start_date", header: "Start Date", type: "date", editable: true, width: "120px" },
  { key: "duration_years", header: "Years", type: "number", editable: true, width: "60px", decimals: 0 },
  { key: "duration_months", header: "Months", type: "number", editable: true, width: "70px", decimals: 0 },
  { key: "duration_days", header: "Days", type: "number", editable: true, width: "60px", decimals: 0 },
  {
    key: "entry_type", header: "Type", type: "dropdown", editable: true, width: "140px",
    options: [
      { value: "base_rent", label: "Base Rent" },
      { value: "rent_step", label: "Rent Step" },
      { value: "cpi_escalation", label: "CPI Escalation" },
    ],
  },
  {
    key: "units", header: "Units", type: "dropdown", editable: true, width: "120px",
    options: [
      { value: "$/SF/Year", label: "$/SF/Year" },
      { value: "$/SF/Month", label: "$/SF/Month" },
      { value: "$/Year", label: "$/Year" },
      { value: "$/Month", label: "$/Month" },
      { value: "%", label: "%" },
    ],
  },
  { key: "amount", header: "Amount", type: "currency", editable: true, width: "100px", decimals: 2 },
];

const computedScheduleColumns = [
  { key: "date", header: "Date", type: "date" as const, width: "100px" },
  { key: "event_type", header: "Event", type: "text" as const, width: "120px" },
  { key: "description", header: "Description", type: "text" as const },
  { key: "annual_rent", header: "Annual Rent", type: "currency" as const, width: "120px" },
  { key: "rate_area", header: "Rent PSF", type: "currency" as const, width: "100px" },
];

// ---------- component ----------

export default function RentRollDetailPanel({
  isOpen,
  onClose,
  sorted,
  detailTenantIdx,
  detailTab,
  onTabChange,
  detailData,
  detailEntries,
  detailDirty,
  detailSaving,
  detailLoading,
  onPrev,
  onNext,
  onEntryChange,
  onEntryAdd,
  onEntryDelete,
  onSave,
  onReset,
}: RentRollDetailPanelProps) {
  const tenant = sorted[detailTenantIdx];

  // --- Free Rent local state ---
  const [freeRentPeriods, setFreeRentPeriods] = useState<FreeRentPeriod[]>([]);

  const handleFreeRentChange = useCallback(
    (id: string, key: keyof FreeRentPeriod, value: unknown) => {
      setFreeRentPeriods((prev) =>
        prev.map((p) => (p.id === id ? { ...p, [key]: value } : p)),
      );
    },
    [],
  );

  const handleFreeRentAdd = useCallback(() => {
    const blank: FreeRentPeriod = { id: crypto.randomUUID(), start_date: "", end_date: "", type: "full", partial_amount: null };
    setFreeRentPeriods((prev) => [...prev, blank]);
  }, []);

  const handleFreeRentDelete = useCallback((id: string) => {
    setFreeRentPeriods((prev) => prev.filter((p) => p.id !== id));
  }, []);

  // --- Percentage Rent state (seeded from the tenant's parsed fields) ---
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal } = useDealContext();
  const toast = useToastContext();

  const [pctRentType, setPctRentType] = useState<PercentageRentType>("none");
  const [pctRentBreakpoint, setPctRentBreakpoint] = useState<number | null>(null);
  const [pctRentRate, setPctRentRate] = useState<number | null>(null);
  const [pctRentEntries, setPctRentEntries] = useState<PercentageRentEntry[]>([]);
  const [pctRentDirty, setPctRentDirty] = useState(false);
  const [pctRentSaving, setPctRentSaving] = useState(false);

  // Seed % Rent state from the tenant's AI-extracted fields whenever the
  // active tenant changes. Engine semantics: rate set + breakpoint null
  // means a natural breakpoint; rate is stored as a decimal (0.06 = 6%)
  // but displayed in the UI as a percentage.
  useEffect(() => {
    const pct = tenant?.percentage_rent_pct;
    if (pct != null && pct > 0) {
      setPctRentType("simple");
      setPctRentRate(pct * 100);
      setPctRentBreakpoint(tenant?.percentage_rent_breakpoint ?? null);
    } else {
      setPctRentType("none");
      setPctRentRate(null);
      setPctRentBreakpoint(null);
    }
    setPctRentEntries([]);
    setPctRentDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.tenant_id, tenant?.percentage_rent_pct, tenant?.percentage_rent_breakpoint]);

  const pctRentConfig = useMemo(
    () => ({
      type: pctRentType,
      breakpoint: pctRentBreakpoint,
      rate_pct: pctRentRate,
      entries: pctRentEntries,
    }),
    [pctRentType, pctRentBreakpoint, pctRentRate, pctRentEntries],
  );

  const handlePctTypeChange = useCallback((type: PercentageRentType) => {
    setPctRentType(type);
    setPctRentDirty(true);
  }, []);

  const handlePctSimpleChange = useCallback(
    (key: "breakpoint" | "rate_pct", value: number | null) => {
      if (key === "breakpoint") setPctRentBreakpoint(value);
      else setPctRentRate(value);
      setPctRentDirty(true);
    },
    [],
  );

  const handlePctEntryChange = useCallback(
    (id: string, key: keyof PercentageRentEntry, value: unknown) => {
      setPctRentEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, [key]: value } : e)),
      );
      setPctRentDirty(true);
    },
    [],
  );

  const handlePctEntryAdd = useCallback(() => {
    const blank: PercentageRentEntry = { id: crypto.randomUUID(), start_date: "", end_date: "", breakpoint: null, rate_pct: null };
    setPctRentEntries((prev) => [...prev, blank]);
    setPctRentDirty(true);
  }, []);

  const handlePctEntryDelete = useCallback((id: string) => {
    setPctRentEntries((prev) => prev.filter((e) => e.id !== id));
    setPctRentDirty(true);
  }, []);

  // Persist % Rent fields to the tenant via the existing PATCH tenant
  // endpoint (resolved by index within property_data.tenants).
  const savePctRent = useCallback(async () => {
    if (!dealId || !tenant) return;
    setPctRentSaving(true);
    try {
      const tenants = ((deal?.property_data as unknown as Record<string, unknown> | undefined)?.tenants ?? []) as Record<string, unknown>[];
      const idx = tenants.findIndex(
        (t) =>
          (t.tenant_id != null && t.tenant_id === tenant.tenant_id)
          || (t.tenant_name === tenant.tenant_name && (t.suite_id ?? null) === (tenant.suite_id ?? null)),
      );
      if (idx < 0) throw new Error("Tenant not found in property data");

      let payload: Record<string, unknown>;
      if (pctRentType === "none") {
        payload = { percentage_rent_pct: null, percentage_rent_breakpoint: null };
      } else if (pctRentType === "simple") {
        payload = {
          percentage_rent_pct: pctRentRate != null ? pctRentRate / 100 : null,
          // null breakpoint = natural breakpoint (annual base rent / rate)
          percentage_rent_breakpoint: pctRentBreakpoint,
        };
      } else {
        // Detailed mode: the engine models a single rate/breakpoint pair, so
        // persist the first schedule entry's values.
        const first = pctRentEntries.find((e) => e.rate_pct != null);
        payload = {
          percentage_rent_pct: first?.rate_pct != null ? first.rate_pct / 100 : null,
          percentage_rent_breakpoint: first?.breakpoint ?? null,
        };
      }

      await updateTenant(dealId, idx, payload);
      // Refresh deal context so the rent roll table and future seeds pick
      // up the saved values.
      const freshDeal = await fetchDeal(dealId);
      setDeal(freshDeal);
      setPctRentDirty(false);
      toast.success("Percentage rent saved");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`Could not save percentage rent: ${friendlyError(msg)}`);
    } finally {
      setPctRentSaving(false);
    }
  }, [dealId, tenant, deal, setDeal, pctRentType, pctRentRate, pctRentBreakpoint, pctRentEntries, toast]);

  // --- Rollover local state (all null = use global defaults) ---
  const [rolloverOverrides, setRolloverOverrides] = useState<RolloverOverrides>(
    () => Object.fromEntries(
      ["renewal_probability", "market_rent_psf", "market_rent_growth",
       "lease_term_years", "downtime_new_months", "downtime_renewal_months",
       "ti_new_psf", "ti_renewal_psf", "lc_new_pct", "lc_renewal_pct",
       "upon_expiration"].map((k) => [k, null]),
    ) as unknown as RolloverOverrides,
  );

  const handleRolloverChange = useCallback(
    (key: keyof RolloverOverrides, value: unknown) => {
      setRolloverOverrides((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // --- Base Rent tab rows (exclude free_rent entries) ---
  const baseRentRows = useMemo(
    () =>
      detailEntries
        .filter((e) => e.entry_type !== "free_rent")
        .map((e) => ({
          id: e.entry_id,
          source: e.source,
          values: {
            start_date: e.start_date,
            duration_years: e.duration_years,
            duration_months: e.duration_months,
            duration_days: e.duration_days,
            entry_type: e.entry_type,
            units: e.units,
            amount: e.amount,
          },
        })),
    [detailEntries],
  );

  const computedRows = useMemo(
    () => (detailData?.computed_schedule as ComputedScheduleRow[]) || [],
    [detailData],
  );

  // --- Loading spinner ---
  const loadingContent = (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-forest-700 border-t-transparent" />
    </div>
  );

  // ---------- tabs ----------
  const tabs = useMemo(
    () => [
      {
        label: "Base Rent",
        content: detailLoading
          ? loadingContent
          : (
            <div className="space-y-6">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-bark-400 mb-2">
                  Base Rent &amp; Rent Steps
                </h3>
                <TimeSeriesInputTable
                  columns={baseRentColumns}
                  rows={baseRentRows}
                  onRowChange={onEntryChange}
                  onRowAdd={onEntryAdd}
                  onRowDelete={onEntryDelete}
                  onReset={onReset}
                  actionButtons={[
                    {
                      label: "Add Rent Step",
                      variant: "primary",
                      rowTemplate: { entry_type: "rent_step", units: "$/SF/Year" },
                    },
                  ]}
                  isDirty={detailDirty}
                  onSave={onSave}
                  isSaving={detailSaving}
                />
              </div>
              {computedRows.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-bark-400 mb-2">
                    Computed Schedule
                  </h3>
                  <ComputedScheduleTable
                    columns={computedScheduleColumns}
                    rows={computedRows}
                  />
                </div>
              )}
            </div>
          ),
      },
      {
        label: "Free Rent",
        content: detailLoading
          ? loadingContent
          : (
            <FreeRentTab
              periods={freeRentPeriods}
              onPeriodChange={handleFreeRentChange}
              onPeriodAdd={handleFreeRentAdd}
              onPeriodDelete={handleFreeRentDelete}
            />
          ),
      },
      {
        label: "% Rent",
        content: (
          <div className="space-y-4">
            <PercentageRentTab
              config={pctRentConfig}
              onTypeChange={handlePctTypeChange}
              onSimpleChange={handlePctSimpleChange}
              onEntryChange={handlePctEntryChange}
              onEntryAdd={handlePctEntryAdd}
              onEntryDelete={handlePctEntryDelete}
            />
            {pctRentDirty && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={savePctRent}
                  disabled={pctRentSaving}
                  className="px-4 py-2 text-sm font-medium bg-forest-700 text-white rounded-lg hover:bg-forest-800 disabled:opacity-50 transition-colors"
                >
                  {pctRentSaving ? "Saving..." : "Save % Rent"}
                </button>
              </div>
            )}
          </div>
        ),
      },
      {
        label: "Rollover",
        content: (
          <RolloverTab
            overrides={rolloverOverrides}
            onChange={handleRolloverChange}
          />
        ),
      },
      { label: "CPI", content: <CpiTab /> },
    ],
    [
      detailLoading, baseRentRows, computedRows, detailDirty, detailSaving,
      onEntryChange, onEntryAdd, onEntryDelete, onReset, onSave,
      freeRentPeriods, handleFreeRentChange, handleFreeRentAdd, handleFreeRentDelete,
      pctRentConfig, handlePctTypeChange, handlePctSimpleChange,
      handlePctEntryChange, handlePctEntryAdd, handlePctEntryDelete,
      pctRentDirty, pctRentSaving, savePctRent,
      rolloverOverrides, handleRolloverChange,
    ],
  );

  if (!tenant) return null;

  return (
    <DetailViewPanel
      isOpen={isOpen}
      onClose={onClose}
      entity={{
        id: tenant.tenant_id,
        name: tenant.tenant_name,
        subtitle: tenant.suite_id ? `Suite ${tenant.suite_id}` : undefined,
      }}
      onPrev={onPrev}
      onNext={onNext}
      tabs={tabs}
      activeTab={detailTab}
      onTabChange={onTabChange}
      hasUnsavedChanges={detailDirty || pctRentDirty}
    />
  );
}
